import cookie from '@s-ui/js/lib/cookie'
import {getDecision} from '@s-ui/pde'

import {stripSlash} from '../../helpers/index.js'
import {addNamesToExcludedVehicles, getMakesWithModelsOptions, getPageUrlFAQsInfo} from '../AdsList/utils.js'

const DEFAULT_PAGINATION_PLACEHOLDER_URL = '#'
const ASPECT_RATIO = 1.33
const MOBILE_WIDTH = 560
const DEFAULT_PHOTO = 'images/no-photo.png'
export const PAGINATION_PLACEHOLDER = '%{pageNumber}'

const getCroppedImageSrc = ({src, width}) => {
  const aspectRatioHeight = Math.floor(width / ASPECT_RATIO)
  const crop = `${width}x${aspectRatioHeight}cut`
  return `${src}/${crop}/`
}

export const NOTIFICATIONS = {
  AD_NOT_AVAILABLE: 'AD_NOT_AVAILABLE',
  HAS_SAVED_FAVORITE_SUCCESS: 'HAS_SAVED_FAVORITE_SUCCESS',
  HAS_SAVED_FAVORITE_ERROR: 'HAS_SAVED_FAVORITE_ERROR'
}
const PROMISE_REJECTED = 'rejected'

const _getStructuredData = ({scripts}) => scripts.map(script => JSON.parse(script.innerHTML))

export async function getInitialProps({routeInfo, context: {i18n, cookies, domain, pde}}) {
  const {
    location: {pathname: path, query, search: queryParams, state: {lastFilter, origin} = {}}
  } = routeInfo

  const cookiesData = cookie.parse(cookies)
  const {AdNotAvailable} = cookiesData
  const hasComeFromAdNotAvailable = Boolean(AdNotAvailable)
  const hasNotAvailableNotification = Boolean(hasComeFromAdNotAvailable)

  const {culture} = i18n
  const config = domain.get('config')
  const {decide} = getDecision(pde)
  const {
    PAGE_NAMES_BY_LISTING_SECTION_ID,
    ADS_LIST_BUSINESS_SUBSCRIPTION_PAGE_NAME,
    ADS_LIST_BUSINESS_SUBSCRIPTION_PATH_PATTERN
  } = config.get('page')
  const url = path + queryParams

  const {
    bodyTypes: bodyTypesConfig,
    CATEGORY2_MAP,
    FILTER_NAMES,
    DEFAULT_RENTING_SEARCH,
    fuelTypes: fueltypesConfig,
    HAS_RECOMMENDER: isRecommenderActive,
    specificFueltypes: specificFueltypesConfig,
    CREATE_ALERT_QUERY_PARAM
  } = config.get('search')

  const provincesConfig = config.get('provinces')

  const {
    COOKIES: {IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION, VISITED_AD: visitedAdCookieKey},
    LISTING_SECTIONS_IDS,
    QUERY_PARAMS,
    RECOMMENDED_ADS
  } = config.get('COMMON')

  const {
    [IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION]: isViewedSortManagementNotification,
    [visitedAdCookieKey]: visitedAdId
  } = cookiesData

  const [
    {
      value: [searchDataResponseError, searchDataResponse]
    },
    {
      value: [subscriptionMakesError, subscriptionMakesResponse]
    },
    {
      value: [subscriptionModelsError, subscriptionModelsResponse]
    },
    {
      value: [pageFaqsError, pageFaqs]
    },
    {
      value: [faqsForUrlSearchError, faqsForUrlSearchResponse]
    }
  ] = await Promise.allSettled([
    domain.get('get_search_data_from_url_search_use_case').execute({url}),
    domain.get('get_subscription_makes_vehicles_use_case').execute(),
    domain.get('get_subscription_models_vehicles_use_case').execute(),
    domain.get('get_static_faqs_seo_use_case').execute({path}),
    domain.get('get_faqs_for_url_search_use_case').execute({targetUrl: path})
  ])
  const getMakeModelError = subscriptionMakesError || subscriptionModelsError
  if (getMakeModelError) throw getMakeModelError
  if (searchDataResponseError) console.error(searchDataResponseError)

  const {breadcrumbs, canonical, listingSectionId, search} = searchDataResponse?.toFiltersSearch() || {}

  const {enabled: isAIFaqsForSearchEnabled} = decide('ff_web_growth_ai_faqs', {queryString: queryParams})

  const initialSearch = search || DEFAULT_RENTING_SEARCH

  const {
    bodyTypeIds = [],
    category2Id,
    categoryId,
    city,
    commitmentMonths: filteredCommitmentMonths = [],
    entry,
    filteredOfferTypeId,
    fuelTypeIds = [],
    litres: {from: minLitres, to: maxLitres} = {},
    offerTypeId,
    fee: {to: maxPrice},
    provinceIds = [],
    region,
    sellerTypeId,
    sortBy,
    sortOrder,
    vehicles = [],
    subscriptionVehicleState: vehicleState,
    targetBuyer,
    year: {from: minYear, to: maxYear} = {}
  } = initialSearch

  const [firstSelectedVehicle = {}] = vehicles

  // Hack until we create a new listingSectionId for business subscription
  const isRentingForBusiness = url.includes(ADS_LIST_BUSINESS_SUBSCRIPTION_PATH_PATTERN)

  const pageName = isRentingForBusiness
    ? ADS_LIST_BUSINESS_SUBSCRIPTION_PAGE_NAME
    : PAGE_NAMES_BY_LISTING_SECTION_ID[listingSectionId] ||
      PAGE_NAMES_BY_LISTING_SECTION_ID[LISTING_SECTIONS_IDS.RENTING_VEHICLES]

  const canonicalPathname = canonical && searchDataResponse?.getCanonicalPathname()
  const isAiFaqsForUrlSearchAllowed =
    isAIFaqsForSearchEnabled && search && stripSlash(path) === stripSlash(canonicalPathname)

  const recommendedAdsConfig = RECOMMENDED_ADS.adsList
  const {maxRecommendations, minRecommendations, minResultsToShowRecommendations} = recommendedAdsConfig

  const {items: subscriptionMakes = []} = subscriptionMakesResponse || {}
  const {items: subscriptionModels = []} = subscriptionModelsResponse || {}

  const _getPagination = () =>
    domain.get('get_search_pagination_url_ads_use_case').execute({
      pageName,
      paginationPlaceholder: PAGINATION_PLACEHOLDER,
      queryParams,
      search: initialSearch,
      url: path
    })

  const _getVehiclesList = () => {
    return domain.get('get_search_results_ads_use_case').execute({
      ...initialSearch,
      listingSectionId,
      withAggregations: true
    })
  }

  const [searchResultsError, searchResults] = await _getVehiclesList()

  const getVisitedAdData = () => {
    const position = searchResults?.items?.findIndex(({id}) => id === visitedAdId) ?? -1
    const isAccessible = position >= 0
    return {id: visitedAdId, isAccessible, position}
  }

  const visitedAdData = getVisitedAdData()

  const _getRecommendedAds = async () => {
    if (!visitedAdId || !isRecommenderActive) return {}

    cookie.remove(visitedAdCookieKey)

    const isFewResultsPage = searchResults?.totalResults <= minResultsToShowRecommendations

    if (!isFewResultsPage && !visitedAdData.isAccessible) return {}

    const [recommendedAdsError, recommendedAdsResponse] = await domain
      .get('get_recommended_ads_use_case')
      .execute({adId: visitedAdId, maxItems: maxRecommendations})

    if (recommendedAdsError) return

    if (recommendedAdsResponse.items.length >= minRecommendations) {
      return recommendedAdsResponse
    }
  }

  const _getMinPrice = ({searchResults = {}}) => {
    return searchResults?.items?.reduce((previousMin, currentAd) => {
      const {monthlySubscriptionFee} = currentAd

      return monthlySubscriptionFee < previousMin || previousMin === 0 ? monthlySubscriptionFee : previousMin
    }, 0)
  }

  const minPrice = _getMinPrice({searchResults})

  const _getBodyTypeLiteral = id => {
    if (!id) return ''
    const bodyTypeLiteral = bodyTypesConfig.find(bodyTypeConfig => Number(bodyTypeConfig.id) === Number(id))?.label
    return i18n.t(bodyTypeLiteral)
  }

  const _getProvinceLiteral = id => provincesConfig.find(provinceConfig => provinceConfig.id === id)?.label

  const _getFuelTypeLiteral = id => {
    if (!id) return ''
    const config = specificFueltypesConfig || fueltypesConfig
    const fuelTypeLiteral = config.find(fuelTypeConfig => Number(fuelTypeConfig.id) === Number(id))?.label

    return i18n.t(fuelTypeLiteral)
  }

  const getMakesWithModels = () => domain.get('get_makes_with_models_vehicles_use_case').execute()

  const _maybeCreateAlert = () => {
    return query[CREATE_ALERT_QUERY_PARAM]
      ? domain.get('create_alert_user_alert_use_case').execute({search: initialSearch})
      : Promise.resolve()
  }

  const _getTrackingData = () => {
    return domain.get('get_page_tracking_data_use_case').execute({
      advertisements: searchResults?.items,
      lastFilter,
      listingSectionId,
      ...(origin && {origin}),
      page: pageName,
      pageNumber: initialSearch?.page,
      search: initialSearch,
      searchResultsCount: searchResults?.totalResults
    })
  }

  // eslint-disable-next-line react/prop-types
  const _getCategory2Literal = ({categoryId, category2Id}) => {
    const items = CATEGORY2_MAP[categoryId]
    const category2Literal = items?.find(category2 => category2.id === category2Id)?.label
    return category2Literal ? i18n.t(category2Literal) : null
  }

  const _getSeoDataAdsList = () => {
    const urlStatics = config.get('urlStatics')
    const initialResultsResponse = searchResults

    const hasResults = Boolean(initialResultsResponse?.totalResults) && Boolean(initialResultsResponse?.items?.length)
    const countTotalResults = hasResults && initialResultsResponse.totalResults
    const nResults = countTotalResults ? i18n.n(countTotalResults) : null
    const maxPriceLocal = maxPrice ? i18n.n(maxPrice) : null
    const minPriceLocal = minPrice ? i18n.n(minPrice) : null
    const [firstResult] = hasResults ? initialResultsResponse.items : []
    const width = MOBILE_WIDTH
    const firstResultHasImage = hasResults && Boolean(firstResult.img)
    const defaultPhotoSrc = `${urlStatics}${DEFAULT_PHOTO}`
    const firstImageSrc = firstResultHasImage ? getCroppedImageSrc({src: firstResult.img, width}) : defaultPhotoSrc

    const getSeoDataUseCaseParams = {
      culture,
      pageName,
      params: {
        bodyType: _getBodyTypeLiteral(bodyTypeIds[0]), // TODO: think about deprecating this type of param in order to centralize more the seo data calcs (MTR-56077)
        bodyTypeIds,
        breadcrumb: breadcrumbs,
        canonical,
        category2: _getCategory2Literal({categoryId, category2Id}), // TODO: to be reviewed in MTR-56077
        categoryId,
        city,
        commitmentMonths: filteredCommitmentMonths,
        entry,
        fuelType: _getFuelTypeLiteral(fuelTypeIds[0]), // TODO: to be reviewed in MTR-56077m
        isCrawlableAndIndexable: false,
        listingSectionId,
        make: firstSelectedVehicle?.make,
        maxPrice: {
          value: maxPrice,
          text: maxPriceLocal
        },
        minPrice: minPriceLocal,
        model: firstSelectedVehicle?.model?.name,
        nResults,
        sortBy,
        sortOrder,
        offerTypeId,
        filteredOfferTypeId,
        page: query.pg,
        path,
        province: _getProvinceLiteral(provinceIds[0]), // TODO: to be reviewed in MTR-56077
        region,
        sellerTypeId,
        targetBuyer,
        version: firstSelectedVehicle?.version,
        subscriptionVehicleState: vehicleState,
        year: minYear || maxYear,
        litres: minLitres || maxLitres
      },
      preloadImages: [firstImageSrc]
    }

    return domain.get('get_seo_data_use_case').execute(getSeoDataUseCaseParams)
  }

  const _getAiFaqsForUrlSearch = () => {
    const hasResults = Boolean(searchResults?.items?.length)

    return isAiFaqsForUrlSearchAllowed && hasResults
      ? domain.get('get_ai_faqs_for_url_search_use_case').execute({listingSectionId, pageType: pageName, search, path})
      : Promise.resolve([])
  }

  const [
    {
      value: [, makesWithModels]
    },
    trackingData,
    {
      value: [, commitmentOptionsResponse]
    },
    {
      value: [paginationError, paginationUrl]
    },
    {value: [seoDataError, seoDataResponse] = []},
    {status: maybeCreateAlertStatus, value: createdAlert},
    {value: recommendedAds},
    {value: [aiFaqsError, aiFaqsResponse] = []}
  ] = await Promise.allSettled([
    getMakesWithModels(),
    _getTrackingData(),
    domain.get('get_subscription_commitment_options_ads_use_case').execute(),
    _getPagination(),
    _getSeoDataAdsList(),
    _maybeCreateAlert(),
    _getRecommendedAds(),
    _getAiFaqsForUrlSearch()
  ])

  const [faqsForUrlError, faqsForUrlResponse] = aiFaqsResponse
    ? [aiFaqsError, aiFaqsResponse]
    : [faqsForUrlSearchError, faqsForUrlSearchResponse]

  const {faqsForUrlSectionTitle, faqsForUrlItems} = getPageUrlFAQsInfo({
    i18n,
    faqsForUrlError,
    faqsForUrlResponse,
    path
  })

  const createAlertError = maybeCreateAlertStatus === PROMISE_REJECTED

  const {head: helmet = {}, h1} = seoDataResponse || {}
  const {scripts: structuredDataScripts = []} = helmet

  const structuredDataJsonList = _getStructuredData({
    scripts: structuredDataScripts
  })

  const paginationPlaceholderUrl = paginationError ? DEFAULT_PAGINATION_PLACEHOLDER_URL : paginationUrl
  // mapping commitment options response to commitment options for commitments' filter component
  const {hasNoCommitmentOption, commitmentMonths} = commitmentOptionsResponse || {}

  const commitmentOptions = commitmentOptionsResponse ? [hasNoCommitmentOption && 0, ...commitmentMonths] : []

  const makeId = firstSelectedVehicle?.makeId
  const tagclouds = await domain.get('get_tagcloud_seo_use_case').execute({
    canonical,
    makes: subscriptionMakes,
    ...(makeId && {makeId, models: subscriptionModels}),
    pageName
  })

  const makesWithModelsOptions = getMakesWithModelsOptions({makesWithModels: makesWithModels?.value})

  return {
    breadcrumb: breadcrumbs,
    h1,
    helmet,
    initialSearch: {
      ...initialSearch,
      [FILTER_NAMES.EXCLUDED_VEHICLES]: addNamesToExcludedVehicles({
        excludedVehicles: initialSearch[FILTER_NAMES.EXCLUDED_VEHICLES],
        makesOptions: makesWithModelsOptions
      })
    },
    lastFilter,
    listFiltersOptions: {
      [FILTER_NAMES.VEHICLES]: {
        allowExcludeFilter: true,
        options: makesWithModelsOptions
      },
      [FILTER_NAMES.MONTHLY_SUBSCRIPTION_COMMITMENT]: {
        options: commitmentOptions
      }
    },
    listingSectionId,
    notifications: {
      [NOTIFICATIONS.AD_NOT_AVAILABLE]: hasNotAvailableNotification
    },
    pageErrors: {
      createAlertError,
      faqsForUrlError,
      pageFaqsError,
      searchResultsError,
      seoDataError,
      subscriptionMakesError,
      subscriptionModelsError
    },
    paginationPlaceholderUrl,
    pageName,
    searchResults,
    pageFaqs,
    faqsForUrlItems,
    faqsForUrlSectionTitle,
    showSortManagementNotification: !isViewedSortManagementNotification,
    structuredDataJsonList,
    subscriptionMakes,
    subscriptionModels,
    tagclouds,
    trackingData,
    createdAlert,
    hasNoMatchesUserAlertInformation: Boolean(query[QUERY_PARAMS.ALERTS.NO_MATCHES]),
    isRecommendedAdsFallback: recommendedAds?.isRecommendedAdsFallback || false,
    recommendedAds,
    visitedAdData
  }
}
